/**
 * Cookie banner vocabulary and its Consent Mode v2 mapping.
 *
 * Ported from little-headless (`lib/cookie-consent.ts` + `rememberMetaClick`).
 * vanilla-cookieconsent records the decision in `cc_cookie`. This module does
 * not own that cookie. It maps the accepted category ids onto Google's
 * signals, the `orsane_marketing_consent` cookie Meta CAPI reads (PR #43),
 * and a first-party `_fbc` built from `fbclid`.
 *
 * `index.html` replays `cc_cookie` in an inline script before GTM loads, which
 * is earlier than this module can run. That script is a second copy of
 * `consentFromCategories` and of the marketing-cookie write. The unit test
 * executes the real script against this module so the two cannot drift.
 */

export type CookieCategoryId = "essential" | "preferences" | "statistics" | "marketing";

export const COOKIE_CATEGORIES: {
    id: CookieCategoryId;
    label: string;
    description: string;
    required?: boolean;
}[] = [
    {
        id: "essential",
        label: "Essential",
        description:
            "Required for the site and the app to work, including security and keeping you signed in. Always on.",
        required: true,
    },
    {
        id: "preferences",
        label: "Preferences",
        description: "Remembers choices that change how the site behaves.",
    },
    {
        id: "statistics",
        label: "Statistics",
        description: "Helps us understand how visitors use Orsane, including Google Analytics. Off until you accept.",
    },
    {
        id: "marketing",
        label: "Marketing",
        description:
            "Used to measure advertising, including Meta. Off until you accept. Accepting stores a marketing-consent cookie shared by www.orsane.app and the app.",
    },
];

export type ConsentState = {
    ad_storage: "granted" | "denied";
    ad_user_data: "granted" | "denied";
    ad_personalization: "granted" | "denied";
    analytics_storage: "granted" | "denied";
    functionality_storage: "granted" | "denied";
    personalization_storage: "granted" | "denied";
    security_storage: "granted";
};

/** vanilla-cookieconsent's own cookie. Same name on www and my so one decision covers both. */
export const CONSENT_COOKIE_NAME = "cc_cookie";

/**
 * Read by Meta Conversions API (PR #43, `MARKETING_CONSENT_COOKIE`).
 * Value `granted` is the only grant. Anything else, including a missing cookie, is not consent.
 */
export const MARKETING_CONSENT_COOKIE = "orsane_marketing_consent";
export const MARKETING_CONSENT_VALUE = "granted";

/** Meta's click-id cookie. Written only with marketing consent, so an ad blocker cannot drop the click. */
export const FBC_COOKIE = "_fbc";

/** Ninety days, Meta's own lifetime for `_fbc`. */
export const FBC_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

/** Matches the banner cookie (`expiresAfterDays: 365`). */
export const MARKETING_CONSENT_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

export const CONSENT_COOKIE_EXPIRES_DAYS = 365;

/** Shared parent domain. Host-only on localhost and dev hosts (`my.orsane.dev`). */
export const SHARED_COOKIE_DOMAIN = ".orsane.app";

/** On every `cookie_consent_update` push, so GTM can tell this banner from any other. */
export const CONSENT_PLATFORM = "vanilla-cookieconsent";

export const OPEN_COOKIE_SETTINGS_EVENT = "orsane:open-cookie-settings";

const FBCLID_PATTERN = /^[A-Za-z0-9_-]{1,200}$/;

/**
 * The four categories translated into Google's seven signals.
 *
 * Anything not named is denied: an unknown category id, a truncated cookie and
 * a visitor who refused all read the same way, which is the only safe direction
 * to fail in.
 */
export function consentFromCategories(accepted: readonly string[]): ConsentState {
    const has = (id: CookieCategoryId) => (accepted.includes(id) ? "granted" : "denied");
    const marketing = has("marketing");
    const preferences = has("preferences");
    return {
        ad_storage: marketing,
        ad_user_data: marketing,
        ad_personalization: marketing,
        analytics_storage: has("statistics"),
        functionality_storage: preferences,
        personalization_storage: preferences,
        security_storage: "granted",
    };
}

/**
 * `.orsane.app` on the production registrable domain so www and my share the
 * banner decision and the marketing-consent cookie. Any other host (localhost,
 * `my.orsane.dev`, a preview URL) gets a host-only cookie, which the browser
 * will actually store.
 */
export function sharedCookieDomain(hostname: string): string | undefined {
    const host = hostname.trim().toLowerCase().replace(/\.$/, "");
    if (host === "orsane.app" || host.endsWith(".orsane.app")) {
        return SHARED_COOKIE_DOMAIN;
    }
    return undefined;
}

/** Meta `fbc` subdomain index: `orsane.app` = 1, `www.orsane.app` = 2. */
export function metaSubdomainIndex(hostname: string): number {
    const parts = hostname.split(".").filter(Boolean);
    return Math.max(0, parts.length - 1);
}

export function sanitizeFbclid(value?: string | null): string | undefined {
    if (!value) {
        return undefined;
    }
    const trimmed = value.trim();
    return FBCLID_PATTERN.test(trimmed) ? trimmed : undefined;
}

export function buildFbcFromFbclid(fbclid: string, creationTimeMs: number, subdomainIndex: number): string | undefined {
    const safe = sanitizeFbclid(fbclid);
    if (!safe || !Number.isFinite(creationTimeMs)) {
        return undefined;
    }
    return `fb.${subdomainIndex}.${Math.floor(creationTimeMs)}.${safe}`;
}

export type CookieWrite = { name: string; value: string; maxAge: number } | { name: string; erase: true };

export function serializeCookie(write: CookieWrite, input: { hostname: string; secure: boolean }): string {
    const domain = sharedCookieDomain(input.hostname);
    const parts = ["Path=/", "SameSite=Lax"];
    if (domain) {
        parts.push(`Domain=${domain}`);
    }
    if (input.secure) {
        parts.push("Secure");
    }
    const attrs = parts.join("; ");
    if ("erase" in write) {
        return `${write.name}=; Max-Age=0; ${attrs}`;
    }
    return `${write.name}=${encodeURIComponent(write.value)}; Max-Age=${write.maxAge}; ${attrs}`;
}

/** Set `granted` when marketing is accepted. Clear the cookie when it is not. */
export function marketingConsentCookie(input: {
    accepted: readonly string[];
    hostname: string;
    secure: boolean;
}): string {
    const granted = input.accepted.includes("marketing");
    const write: CookieWrite = granted
        ? { name: MARKETING_CONSENT_COOKIE, value: MARKETING_CONSENT_VALUE, maxAge: MARKETING_CONSENT_MAX_AGE_SECONDS }
        : { name: MARKETING_CONSENT_COOKIE, erase: true };
    return serializeCookie(write, input);
}

/**
 * Keeps an ad's click id in a first-party `_fbc` — with marketing consent, and
 * only then.
 *
 * Without Marketing the cookie is removed, so withdrawing consent withdraws it.
 * With it, an `fbclid` in the address is stored as `fb.<index>.<ms>.<fbclid>`.
 * The same click id seen again on a reload keeps its first timestamp, which is
 * what Meta's format means by it. The index matches Meta's subdomain index so
 * the value is one `isMetaFbc` accepts.
 */
export function rememberMetaClick(input: {
    accepted: readonly string[];
    search: string;
    hostname: string;
    current: string | null;
    now: number;
}): { set: string } | { erase: true } | null {
    if (!input.accepted.includes("marketing")) {
        return input.current ? { erase: true } : null;
    }
    const fbclid = sanitizeFbclid(new URLSearchParams(input.search).get("fbclid"));
    if (!fbclid) {
        return null;
    }
    if (input.current?.endsWith(`.${fbclid}`)) {
        return null;
    }
    const value = buildFbcFromFbclid(fbclid, input.now, metaSubdomainIndex(input.hostname));
    return value ? { set: value } : null;
}

export function fbcCookie(input: {
    accepted: readonly string[];
    search: string;
    hostname: string;
    secure: boolean;
    current: string | null;
    now: number;
}): string | null {
    const action = rememberMetaClick(input);
    if (!action) {
        return null;
    }
    if ("erase" in action) {
        return serializeCookie({ name: FBC_COOKIE, erase: true }, input);
    }
    return serializeCookie({ name: FBC_COOKIE, value: action.set, maxAge: FBC_MAX_AGE_SECONDS }, input);
}

let cookieSettingsOpener: (() => void) | null = null;
let cookieSettingsQueued = false;

/** The banner registers this once `CookieConsent.run` has finished. */
export function registerCookieSettingsOpener(open: () => void): void {
    cookieSettingsOpener = open;
    if (cookieSettingsQueued) {
        cookieSettingsQueued = false;
        open();
    }
}

/** Announce a consent decision to Consent Mode. Decisions made now; a stored decision is replayed by the inline script. */
export function applyConsentUpdate(accepted: readonly string[]): void {
    if (typeof window === "undefined") {
        return;
    }
    const consent = consentFromCategories(accepted);
    const dataLayer = (window.dataLayer ?? []) as unknown[];
    window.dataLayer = dataLayer as Window["dataLayer"];
    if (typeof window.gtag === "function") {
        window.gtag("consent", "update", consent);
    } else {
        dataLayer.push(["consent", "update", consent]);
    }
    dataLayer.push({ event: "cookie_consent_update", consent_platform: CONSENT_PLATFORM });
}

export function openCookieSettings(): void {
    if (cookieSettingsOpener) {
        cookieSettingsOpener();
        return;
    }
    cookieSettingsQueued = true;
    if (typeof window === "undefined") {
        return;
    }
    window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));
}
